/* Instant navigation: on a classic multi-page Shopify site every page-to-page
   click pays the full network + parse + render cost from zero (no SPA/PJAX
   layer exists here). This closes that gap in two layers, each used only
   where the browser actually supports it, falling back cleanly otherwise:

   1) Speculation Rules API (Chromium): tells the browser to fully PRERENDER
      a same-origin page in a hidden background tab. A prerendered page swaps
      in on click with no network/parse/render delay at all — this is what
      makes the difference between "fast" and "instant". Eagerness is set to
      "conservative" (pointerdown/touchstart only, not hover) on purpose:
      prerendering actually executes the target page, including every
      third-party analytics/pixel script on it (Klaviyo, ad pixels, the A/B
      test tool itself, etc.). Those scripts generally don't know to check
      document.prerendering before firing, so a more eager setting — one that
      fires on mere hover — would count a "page view"/exposure for every
      shopper who hovered a link and changed their mind, quietly inflating
      traffic and skewing conversion-rate math for anything measuring this
      site. "conservative" only fires immediately before a real navigation
      commitment, so it keeps the speed win without that side effect.
   2) Cross-document View Transitions (Chromium): a smooth cross-fade between
      the old and new page instead of the default hard white-flash reload —
      the "smooth" half of the request. Pure CSS, silently ignored by
      browsers that don't understand it.

   3) <link rel=prefetch> on hover/touch (broad support: Firefox, Safari,
      older Chromium): only runs when Speculation Rules isn't available, so
      supporting and non-supporting browsers never double up work for the
      same link. Prefetching alone can't prerender, but it still shaves the
      connection + download time off the eventual navigation.

   Deliberately conservative throughout — only same-origin, GET-safe page
   links, never cart/account/checkout/password (state-changing or
   session-sensitive) routes, opt-out via a data-no-instant attribute on any
   link, and it backs off entirely on Save-Data / slow connections so it
   never costs data-conscious or slow-connection shoppers anything. */
(function () {
  var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (connection) {
    if (connection.saveData) return;
    if (/2g/.test(connection.effectiveType || '')) return;
  }

  /* ── Layer 2: smooth cross-document transitions ──
     Chromium-only by design — gated on navigator.userAgentData, an API
     that only real Chromium engines implement (Safari/WebKit, including
     Chrome for iOS which also runs on WebKit, do not expose it). This
     used to be a plain unconditional CSS injection on the assumption
     that unsupporting browsers would just ignore the unknown at-rule,
     but WebKit now recognizes "@view-transition" and its cross-document
     implementation has a bug that leaves the page's scrollable height
     larger than the visible content after a navigation — a sustained
     blank area scrollable past the footer, on every page, in any
     WebKit-based mobile browser. Restricting this to Chromium avoids it
     entirely while keeping the intended effect where it actually works. */
  if (navigator.userAgentData) {
    var transitionStyle = document.createElement('style');
    transitionStyle.textContent = '@view-transition { navigation: auto; }';
    document.head.appendChild(transitionStyle);
  }

  /* ── Layer 1: full prerendering via Speculation Rules ── */
  var supportsSpeculationRules =
    'HTMLScriptElement' in window &&
    typeof HTMLScriptElement.supports === 'function' &&
    HTMLScriptElement.supports('speculationrules');

  if (supportsSpeculationRules) {
    var rulesScript = document.createElement('script');
    rulesScript.type = 'speculationrules';
    rulesScript.textContent = JSON.stringify({
      prerender: [
        {
          source: 'document',
          where: {
            and: [
              { href_matches: '/*' },
              { not: { href_matches: '/cart*' } },
              { not: { href_matches: '/account*' } },
              { not: { href_matches: '/checkout*' } },
              { not: { href_matches: '/password*' } },
              { not: { selector_matches: '[data-no-instant]' } },
              { not: { selector_matches: '[target="_blank"]' } },
            ],
          },
          /* "conservative" = only prerender once the browser sees a strong
             navigation signal (pointerdown on desktop, touchstart on mobile)
             — essentially the instant before the click completes. Still
             enough lead time to erase the network/parse/render delay, but it
             does not fire on hover alone, so idle cursor movement over
             product links can no longer trigger a background page load /
             analytics exposure for a page the shopper never actually visits.
             See the file header comment for why that matters here. */
          eagerness: 'conservative',
        },
      ],
    });
    document.head.appendChild(rulesScript);
    /* Prerendering already covers everything prefetch would — running both
       for the same link would just be wasted duplicate work. */
    return;
  }

  /* ── Layer 3: hover/touch <link rel=prefetch> fallback ── */
  var supportsPrefetch = (function () {
    var link = document.createElement('link');
    return link.relList && link.relList.supports && link.relList.supports('prefetch');
  })();
  if (!supportsPrefetch) return;

  var prefetched = new Set();
  var hoverTimer = null;
  var HOVER_DELAY = 30; // ms — just enough to skip a cursor merely passing over the link
  var MAX_PREFETCH = 24; // hard cap so a long session can't quietly balloon memory/requests

  var EXCLUDE_PATH = /^\/(cart|account|checkout|password)(\/|$)/;

  function isPrefetchable(anchor) {
    if (anchor.hasAttribute('data-no-instant') || anchor.target === '_blank') return null;
    var a;
    try {
      a = new URL(anchor.href, location.href);
    } catch (e) {
      return null;
    }
    if (a.origin !== location.origin) return null;
    /* Same page, just an in-page anchor jump (e.g. "#section") — nothing to prefetch. */
    if (a.pathname === location.pathname && a.search === location.search) return null;
    if (EXCLUDE_PATH.test(a.pathname)) return null;
    if (/\.(pdf|zip|jpg|jpeg|png|gif|svg|mp4|webm)$/i.test(a.pathname)) return null;
    return a.href;
  }

  function prefetch(anchor) {
    var clean = isPrefetchable(anchor);
    if (!clean || prefetched.has(clean) || prefetched.size >= MAX_PREFETCH) return;
    prefetched.add(clean);
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = clean;
    link.as = 'document';
    document.head.appendChild(link);
  }

  function onPointerOver(event) {
    var anchor = event.target.closest('a[href]');
    if (!anchor) return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(function () {
      prefetch(anchor);
    }, HOVER_DELAY);
  }

  function onPointerOut(event) {
    if (event.target.closest && event.target.closest('a[href]')) clearTimeout(hoverTimer);
  }

  /* Belt-and-suspenders for fast clickers who click before the 30ms hover
     delay even fires (or click via a screen reader / assistive input that
     skips hover entirely): mousedown fires right before click, so this still
     lands the request a few ms ahead of the actual navigation starting. */
  function onMouseDown(event) {
    var anchor = event.target.closest('a[href]');
    if (anchor) prefetch(anchor);
  }

  /* Touch has no hover — start the moment a finger lands, which still shaves
     the connection/TLS/TTFB time off the eventual navigation. */
  function onTouchStart(event) {
    var anchor = event.target.closest('a[href]');
    if (anchor) prefetch(anchor);
  }

  document.addEventListener('mouseover', onPointerOver, { passive: true });
  document.addEventListener('mouseout', onPointerOut, { passive: true });
  document.addEventListener('mousedown', onMouseDown, { passive: true });
  document.addEventListener('touchstart', onTouchStart, { passive: true });
  document.addEventListener('focus', onPointerOver, { passive: true, capture: true });
})();
 